import { convertCloserAddressToFCTAddress } from '@core/util/address';
import { GetPrimaryTitle, GetTitlesSorted } from '@core/util/matter';
import { centsToDollars } from '@core/util/money';
import {
	getMortgageLenderNamesForTitleInsurance,
	getPriorityNumber,
} from '@core/util/mortgage';
import { stringToNumericId } from '@core/util/string';
import { BusinessClient, ClientType, PersonClient } from '@models/clients';
import {
	ActingForType,
	Brokerage,
	BusinessMatterClient,
	CondoUnit,
	InterestOptions,
	MatterFileType,
	MatterPropertyDetails,
	MatterTitle,
	ParcelUnit,
	PersonMatterClient,
	PropertyStatus,
	PropertyType,
} from '@models/matter';
import { LawFirm } from '@models/lawFirms';
import {
	MatterMortgage,
	MatterMortgagePriority,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import { isSet } from '@core/util/value';
import {
	FCTMortgage,
	FCTOwner,
	FCTParcel,
	FCTPin,
} from '@models/title-insurance/fct';
import { getCondoUnitLegalDescription } from '@core/util/parcels';

const ParcelSequence = [
	'A',
	'B',
	'C',
	'D',
	'E',
	'F',
	'G',
	'H',
	'I',
	'J',
	'K',
	'L',
	'M',
	'N',
	'O',
	'P',
	'Q',
	'R',
	'S',
	'T',
	'U',
	'V',
	'W',
	'X',
	'Y',
	'Z',
];

export const getBrokerage = (clientBrokerage: Brokerage) => ({
	firmName: clientBrokerage?.name,
	firstName: clientBrokerage?.broker?.firstName,
	lastName: clientBrokerage?.broker?.lastName,
	phone:
		clientBrokerage?.broker?.workPhone?.substring(0, 24) ??
		clientBrokerage?.broker?.phoneNumber?.substring(0, 24) ??
		clientBrokerage?.phoneNumber?.substring(0, 24),
});

export const getVendorSolicitor = (otherPartyLawFirm: LawFirm) => ({
	firmName: otherPartyLawFirm?.name,
	firstName: otherPartyLawFirm?.lawyer?.firstName,
	lastName: otherPartyLawFirm?.lawyer?.lastName,
});

export const getBorrowerSolicitor = (otherPartyLawFirm: LawFirm) => ({
	firmName: otherPartyLawFirm?.name,
	firstName: otherPartyLawFirm?.lawyer?.firstName,
	lastName: otherPartyLawFirm?.lawyer?.lastName,
	phone:
		otherPartyLawFirm?.lawyer?.phoneNumber?.substring(0, 24) ??
		otherPartyLawFirm?.phoneNumber?.substring(0, 24),
});

export const getActingFor = (
	fileType: MatterFileType,
	actingFor: ActingForType,
	mortgages: MatterMortgage[],
): 'Mortgagor' | 'Mortgagee' | 'Both' | null => {
	if (fileType === MatterFileType.REFINANCE) {
		switch (actingFor) {
			case ActingForType.MORTGAGOR:
				return 'Mortgagor';
			case ActingForType.MORTGAGEE:
				return 'Mortgagee';
			default:
				return 'Both';
		}
	} else {
		const primary = mortgages?.find(
			(x) => x.priority === MatterMortgagePriority.PRIORITY_1,
		);
		if (!primary) {
			return null;
		}

		return primary.actingForMortgagee ? 'Both' : 'Mortgagor';
	}
};

export const getMortgage = (
	mortgage: MatterMortgageWithAssociations,
): FCTMortgage => {
	const allLendersNames = getMortgageLenderNamesForTitleInsurance(mortgage);
	const fctMortgage: FCTMortgage = {
		sourceId: mortgage.numericId,
		primary: mortgage.priority === MatterMortgagePriority.PRIORITY_1,
		amount: Number(centsToDollars(mortgage.amount)),
		lenderName: allLendersNames ? allLendersNames : '',
		priority: getPriorityNumber(mortgage.priority),
	};

	if (isSet(mortgage.referenceNumber)) {
		fctMortgage.referenceNumber = mortgage.referenceNumber;
	}

	return fctMortgage;
};

export const getOwners = (
	clients: (PersonMatterClient | BusinessMatterClient)[],
): FCTOwner[] => {
	const owners: FCTOwner[] = [];

	clients.forEach((client) => {
		const owner: FCTOwner = {
			sourceId: stringToNumericId(client.id?.slice(0, 7)),
		};

		if (client.type === ClientType.PERSON) {
			owner.firstName = (client as PersonClient)?.firstName?.substring(0, 100);
			owner.lastName = (client as PersonClient)?.lastName?.substring(0, 200);

			if (isSet((client as PersonClient)?.middleNames)) {
				owner.middleName = (client as PersonClient)?.middleNames?.substring(
					0,
					25,
				);
			}

			if (isSet((client as PersonClient)?.dateOfBirth)) {
				owner.dateOfBirth = (client as PersonClient)?.dateOfBirth;
			}
		} else {
			owner.corporationName = (client as BusinessClient)?.name;
		}

		const address = convertCloserAddressToFCTAddress(client?.reportAddress);

		if (address?.streetAddress1 && address?.province) {
			owner.address = address;
		}

		if (isSet(client?.phone)) {
			owner.phone = client?.phone?.substring(0, 24);
		}

		if (isSet(client?.email)) {
			owner.email = client?.email;
		}

		owners.push(owner);
	});

	return owners;
};

export const getParcels = (
	propertyDetails: MatterPropertyDetails,
	matterTitles: MatterTitle[],
): FCTParcel[] => {
	const parcels: FCTParcel[] = [];
	const estateType = getEstateType(
		propertyDetails?.interestEstate as InterestOptions,
	);

	const primaryPropertyTitle: CondoUnit | ParcelUnit =
		GetPrimaryTitle(matterTitles);
	const titlesSorted: (CondoUnit | ParcelUnit)[] =
		GetTitlesSorted(matterTitles);

	const isCondo = propertyDetails?.propertyType?.type === PropertyType.CONDO;

	let legalDescription;
	if (primaryPropertyTitle) {
		const _legalDesc = isCondo
			? getCondoUnitLegalDescription(primaryPropertyTitle as CondoUnit)
			: primaryPropertyTitle.legalDescription;
		legalDescription = _legalDesc?.slice(0, 4000);
	}

	if (!estateType) {
		return parcels;
	}

	let offset = 0;

	if (primaryPropertyTitle) {
		const parcelA: FCTParcel = {
			estateType,
			sequence: 'A',
		};

		if (legalDescription) {
			parcelA.legalDescription = legalDescription;
		}

		if (isCondo && propertyDetails?.propertyType?.parcelOfTiedLand) {
			parcelA.condominiumPlan = primaryPropertyTitle?.planName ?? '';
		}

		parcels.push(parcelA);

		offset++;
	}

	if (propertyDetails?.propertyType?.parcelOfTiedLand) {
		const legalDescAddition = `, together with an undivided common interest in the ${propertyDetails?.propertyType?.condoPlan}`;

		const legalDescPre = titlesSorted
			.map((parcel) =>
				isCondo
					? getCondoUnitLegalDescription(parcel as CondoUnit)
					: parcel.legalDescription || '',
			)
			.join('; ')
			?.slice(0, 4000 - (legalDescAddition?.length ?? 0));
		const legalDesc = `${legalDescPre?.length > 0 ? legalDescPre : ''}${legalDescAddition}`;
		const newParcel: FCTParcel = {
			sequence: ParcelSequence[parcels.length - 1 + offset],
			estateType: 'Fee Simple',
			legalDescription: legalDesc,
		};

		if (isSet(propertyDetails?.propertyType?.condoPlan)) {
			newParcel.condominiumPlan = propertyDetails?.propertyType?.condoPlan;
		}

		if (isSet(propertyDetails?.propertyType?.percentageInterest)) {
			newParcel.interest = propertyDetails?.propertyType?.percentageInterest;
		}

		parcels.push(newParcel);

		offset++;
	}

	const otherTitles = titlesSorted?.filter(
		(x) => x?.pinNumber !== primaryPropertyTitle?.pinNumber,
	);
	otherTitles?.forEach((x, index) => {
		const parcel: FCTParcel = {
			estateType,
			sequence: ParcelSequence[index + offset],
		};

		const _legalDescription = isCondo
			? getCondoUnitLegalDescription(x as CondoUnit)
			: x.legalDescription; // 4000 character limit

		if (_legalDescription) {
			parcel.legalDescription = _legalDescription;
		}

		if (isCondo && propertyDetails?.propertyType?.parcelOfTiedLand) {
			parcel.condominiumPlan = x.planName ?? '';
		}

		parcels.push(parcel);
	});

	if (otherTitles?.length > 0) {
		offset = offset + otherTitles.length;
	}

	return parcels;
};

export const getEstateType = (
	estateType: InterestOptions,
): 'Easement' | 'Fee Simple' | 'Leasehold' | 'Other' => {
	switch (estateType) {
		case InterestOptions.FEES:
		case InterestOptions.FEEAS:
		case InterestOptions.SURFRT:
		case InterestOptions.MINERT:
			return 'Fee Simple';
		case InterestOptions.LHOLD:
			return 'Leasehold';
		case InterestOptions.EASE:
			return 'Easement';
		default:
			return 'Other';
	}
};

export const getPins = (
	propertyDetails: MatterPropertyDetails,
	matterTitles: MatterTitle[],
): FCTPin[] => {
	const pins: FCTPin[] = [];

	const titles = GetTitlesSorted(matterTitles)?.filter((x) => x.pinNumber);
	let primaryPropertyTitle: MatterTitle = GetPrimaryTitle(matterTitles);

	if (!primaryPropertyTitle && titles?.length > 0) {
		primaryPropertyTitle = titles[0];
	}

	if (isSet(primaryPropertyTitle)) {
		// needs to support more than 1 pin here
		pins.push({
			sourceId: 1,
			value: primaryPropertyTitle.pinNumber,
		});
	}

	const otherPins = titles?.filter(
		(x) => x.pinNumber !== primaryPropertyTitle.pinNumber,
	);

	otherPins?.forEach((x, i) => {
		pins.push({
			sourceId: i + 2,
			value: x.pinNumber,
		});
	});

	return pins;
};

export const getTransactionType = (
	fileType: MatterFileType,
	propertyDetails: MatterPropertyDetails,
): 'Purchase New' | 'Purchase Resale' | 'Mortgage Only' => {
	if (fileType === MatterFileType.REFINANCE) {
		return 'Mortgage Only';
	}

	return propertyDetails?.propertyType?.status === PropertyStatus.NEW
		? 'Purchase New'
		: 'Purchase Resale';
};
